import { useEffect, useState, type ReactNode } from 'react';

import { Menu } from '@mui/icons-material';
import { Box } from '@mui/system';
import { useDebounce } from 'use-debounce';

import { BottomSheet } from 'components/docked';
import { MenuModulesButton } from 'components/header/menu-modules/MenuModulesButton';
import { MenuModulesSearchField } from 'components/header/menu-modules/MenuModulesSearchField';
import { MenuModulesSearchResults } from 'components/header/menu-modules/MenuModulesSearchResults';
import { MenuModulesNavigationStack, MenuModulesPanel, TRANSITION_MS } from 'components/header/menu-modules/mobile/MenuModulesNavigationStack';
import { MenuModulesSubmenuTitle } from 'components/header/menu-modules/mobile/MenuModulesSubmenuTitle';
import { MenuNode } from 'components/header/menu-modules/types';

const SEARCH_DEBOUNCE_MS = 200;

type MenuModulesMobile = {
    menuNodes: MenuNode[];
    flatNodes: MenuNode[];
};

export function MenuModulesMobile({ menuNodes, flatNodes }: MenuModulesMobile) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [debouncedQuery, debounceControl] = useDebounce(query, SEARCH_DEBOUNCE_MS);
    const [stack, setStack] = useState<MenuModulesPanel[]>([{ nodes: menuNodes, title: '' }]);
    const [currentIndex, setCurrentIndex] = useState(0);

    const currentPanel = stack[currentIndex];
    const inSubmenu = currentIndex > 0;
    const isSearching = debouncedQuery.trim().length >= 2;

    const [searchPanelEl, setSearchPanelEl] = useState<HTMLDivElement | null>(null);
    const [navPanelEl, setNavPanelEl] = useState<HTMLDivElement | null>(null);
    const [searchFieldEl, setSearchFieldEl] = useState<HTMLDivElement | null>(null);

    const contentEl = isSearching ? searchPanelEl : navPanelEl;

    /**
     * Remove o último painel da stack.
     */
    const popStack = () => {
        setCurrentIndex((i) => i - 1);

        // aplica timeout para remover o painel da stack após a animação de saída
        setTimeout(() => setStack((p) => p.slice(0, -1)), TRANSITION_MS);
    };

    /**
     * Adiciona um novo painel à stack.
     */
    const pushStack = (node: MenuNode) => {
        setStack((p) => [...p, { nodes: node.children ?? [], title: node.title }]);

        setCurrentIndex((i) => i + 1);
    };

    useEffect(() => {
        if (isSearching) {
            setStack([{ nodes: menuNodes, title: '' }]);

            setCurrentIndex(0);
        }
    }, [isSearching, menuNodes]);

    let drawerTitle: ReactNode = (
        <>
            <Menu />
            Módulos
        </>
    );

    if (inSubmenu) drawerTitle = <MenuModulesSubmenuTitle currentPanel={currentPanel} onBack={popStack} />;

    /**
     * Fecha o drawer, limpa o estado do menu e reseta a busca.
     */
    const onClose = () => {
        setOpen(false);

        setTimeout(() => {
            setStack([{ nodes: menuNodes, title: '' }]);
            setCurrentIndex(0);
            setQuery('');
            debounceControl.flush();
        }, 350);
    };

    return (
        <>
            <MenuModulesButton onClick={() => (open ? onClose() : setOpen(true))} />

            <BottomSheet open={open} onOpen={() => setOpen(true)} onClose={onClose} title={drawerTitle} measureElements={[contentEl, searchFieldEl]}>
                <Box ref={setSearchFieldEl}>
                    <MenuModulesSearchField query={query} setQuery={setQuery} />
                </Box>

                <Box sx={{ overflowX: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {isSearching && (
                        <Box
                            sx={{
                                flex: 1,
                                overflowY: 'auto',
                                WebkitOverflowScrolling: 'touch',
                                touchAction: 'pan-y',
                                overscrollBehavior: 'contain',
                            }}
                        >
                            <Box ref={setSearchPanelEl} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                <MenuModulesSearchResults query={debouncedQuery} flatNodes={flatNodes} onClose={onClose} />
                            </Box>
                        </Box>
                    )}

                    {!isSearching && (
                        <MenuModulesNavigationStack
                            stack={stack}
                            currentIndex={currentIndex}
                            onPush={pushStack}
                            onClose={onClose}
                            setActivePanelEl={setNavPanelEl}
                        />
                    )}
                </Box>
            </BottomSheet>
        </>
    );
}
